import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, message, Tooltip, Space, Tag } from "antd";
import {
  WalletOutlined,
  CopyOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ShareAltOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import {
  FaGem, FaUserFriends, FaSpinner,
  FaShieldAlt
} from "react-icons/fa";
import { getUserWallet, getWalletTransactions, buyPlanFromWallet, getPremiumPlans } from "../../Api/CoreApi";

const Wallet = () => {
  const navigate = useNavigate();
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [buyLoading, setBuyLoading] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [wallet, txData] = await Promise.all([
        getUserWallet(),
        getWalletTransactions()
      ]);
      setWalletData(wallet);
      setTransactions(txData?.transactions || []);
    } catch (error) {
      setWalletData({ balance: 0, total_earned: 0, total_spent: 0 });
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAll();
    message.success("Wallet refreshed!");
    setRefreshing(false);
  };

  const fetchPlans = async () => {
    try {
      const data = await getPremiumPlans();
      setPlans(data || []);
    } catch (err) {
      message.error("Failed to load plans");
    }
  };

  const handleShowPlans = async () => {
    if (!showPlans && plans.length === 0) await fetchPlans();
    setShowPlans(!showPlans);
    setSelectedPlan(null);
  };

  const handleWalletPurchase = async () => {
    if (!selectedPlan) { message.warning("Please select a plan"); return; }
    const plan = plans.find(p => p.id === selectedPlan);
    const price = Number(plan.price);
    const balance = Number(walletData?.balance || 0);

    if (balance < price) {
      message.error(`Insufficient balance! Need ₹${price}, Available ₹${balance}`);
      return;
    }

    setBuyLoading(true);
    try {
      const result = await buyPlanFromWallet(selectedPlan);
      if (result.success) {
        message.success(`🎉 ${plan.plan_name} activated from Wallet!`);
        setSelectedPlan(null);
        setShowPlans(false);
        await fetchAll();
      } else {
        message.error(result.error || "Purchase failed");
      }
    } catch (err) {
      message.error(err?.response?.data?.error || err?.message || "Payment failed");
    } finally {
      setBuyLoading(false);
    }
  };

  const referralLink = `${window.location.origin}/register?ref=${walletData?.referral_code || 'YOUR_CODE'}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink);
    message.success("Referral Link Copied! 📋");
  };

  const getSourceConfig = (source) => {
    const configs = {
      referral: { label: "Referral Commission", icon: "👥", color: "#10b981", bg: "#f0fdf4", border: "#bbf7d0" },
      plan_purchase: { label: "Plan Purchase", icon: "💎", color: "#ff0055", bg: "#fff0f3", border: "#ffd3df" },
    };
    return configs[source] || { label: source || "Transaction", icon: "💰", color: "#888", bg: "#f5f5f5", border: "#e5e5e5" };
  };

  const filteredTransactions = transactions.filter(tx => {
    if (activeTab === "all") return true;
    return tx.transaction_type === activeTab;
  });

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  const balance = Number(walletData?.balance || 0);

  if (loading) {
    return (
      <>
        <style>{`
          @keyframes wSpin { 0% { transform: rotate(0deg) } 100% { transform: rotate(360deg) } }
          .w-loading-container { display: flex; justify-content: center; align-items: center; min-height: 60vh; flex-direction: column; gap: 16px; }
          .w-spinner-icon { color: #ff0055; animation: wSpin 1s linear infinite; }
          .w-loading-container p { color: #888; font-size: 13px; }
        `}</style>
        <div className="w-loading-container">
          <FaSpinner size={36} className="w-spinner-icon" />
          <p>Loading wallet...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
        * { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; }
        
        @keyframes wSpin { 0% { transform: rotate(0deg) } 100% { transform: rotate(360deg) } }
        
        /* ============ LAYOUT ============ */
        .w-page { 
          background: linear-gradient(180deg, #fff 0%, #fffafb 50%, #fff5f7 100%); 
          min-height: 100vh; 
          padding: 20px 16px; 
        }
        .w-wrap { max-width: 1000px; margin: 0 auto; }

        /* ============ BALANCE CARD ============ */
        .wb-card {
          background: linear-gradient(135deg, #ff0055 0%, #ff4d88 40%, #c2185b 100%);
          border-radius: 24px; 
          padding: 32px 28px; 
          margin-bottom: 20px;
          position: relative; 
          overflow: hidden; 
          box-shadow: 0 16px 44px rgba(255,0,85,.28);
        }
        .wb-top { 
          display: flex; 
          justify-content: space-between; 
          margin-bottom: 12px; 
          align-items: center; 
        }
        .wb-label { 
          color: rgba(255,255,255,.9); 
          font-size: 13px; 
          font-weight: 600; 
          text-transform: uppercase; 
          margin: 0; 
        }
        .wb-refresh-btn { 
          background: rgba(255,255,255,.2) !important; 
          color: #fff !important; 
          border: 1px solid rgba(255,255,255,.4) !important; 
        }
        .wb-refresh-btn:hover { 
          background: rgba(255,255,255,.3) !important; 
          color: #fff !important;
        }
        .wb-amount-wrapper { 
          font-size: 52px; 
          font-weight: 900; 
          color: #fff; 
          margin: 0 0 6px; 
          line-height: 1.1; 
        }
        .wb-currency { font-size: 32px; vertical-align: top; }
        .wb-note { 
          color: rgba(255,255,255,.8); 
          font-size: 12px; 
          margin: 0; 
          display: flex; 
          align-items: center; 
          gap: 6px; 
        }

        /* ============ REFERRAL BOX ============ */
        .referral-box {
          background: linear-gradient(135deg, #f0fdf4, #dcfce7); 
          border: 2px dashed #4ade80;
          border-radius: 16px; 
          padding: 18px 20px; 
          margin-bottom: 20px;
          display: flex; 
          align-items: center; 
          justify-content: space-between; 
          flex-wrap: wrap; 
          gap: 15px;
        }
        .refer-text h3 { 
          color: #166534; 
          font-size: 16px; 
          font-weight: 800; 
          margin: 0 0 4px; 
          display: flex; 
          align-items: center; 
          gap: 8px; 
        }
        .refer-text p { 
          color: #15803d; 
          font-size: 12px; 
          margin: 0; 
          font-weight: 500; 
        }
        .refer-input-wrap { flex: 1; min-width: 280px; }
        .refer-input { 
          border: 1px solid #86efac !important; 
          color: #166534 !important; 
          font-weight: 600 !important; 
          background: #fff !important; 
        }
        .refer-copy-btn { 
          background: #16a34a !important; 
          border-color: #16a34a !important; 
        }
        .refer-copy-btn:hover { 
          background: #15803d !important; 
          border-color: #15803d !important;
        }

        /* ============ ACTION CARD ============ */
        .action-grid { 
          margin-bottom: 20px; 
        }
        .action-card {
          display: flex; 
          align-items: center; 
          gap: 14px; 
          padding: 16px 20px; 
          border-radius: 16px;
          border: 2px solid #bfdbfe; 
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          cursor: pointer; 
          transition: all 0.3s ease;
        }
        .action-card:hover { 
          transform: translateY(-3px); 
          box-shadow: 0 8px 20px rgba(59,130,246,0.15);
        }
        .action-icon {
          width: 46px; 
          height: 46px; 
          border-radius: 12px; 
          display: flex; 
          align-items: center; 
          justify-content: center;
          font-size: 20px; 
          background: linear-gradient(135deg, #3b82f6, #60a5fa); 
          color: #fff;
          box-shadow: 0 4px 10px rgba(59,130,246,0.3);
          flex-shrink: 0;
        }
        .action-body { 
          flex: 1;
          min-width: 0;
        }
        .action-title { 
          color: #1e40af; 
          font-size: 15px; 
          font-weight: 800; 
          margin: 0 0 3px 0; 
          line-height: 1.3;
        }
        .action-desc { 
          color: #3b82f6; 
          font-size: 12px; 
          margin: 0; 
          font-weight: 500; 
          line-height: 1.3;
        }
        .action-arrow { 
          margin-left: auto; 
          font-size: 20px; 
          color: #3b82f6; 
          font-weight: bold;
          flex-shrink: 0;
        }

        /* ============ PLANS SECTION ============ */
        .plans-wrapper {
          background: #fff; 
          border: 2px solid #ffe4ec; 
          border-radius: 20px; 
          overflow: hidden;
          margin-bottom: 20px; 
          box-shadow: 0 4px 20px rgba(255,0,85,0.08);
        }
        .plans-header {
          display: flex; 
          justify-content: space-between; 
          padding: 16px 20px;
          border-bottom: 2px solid #fff0f3; 
          background: linear-gradient(135deg, #fffafc, #fff5f7); 
          align-items: center;
        }
        .plans-header h4 { 
          font-size: 16px; 
          font-weight: 800; 
          color: #1a0a10; 
          margin: 0; 
          display: flex; 
          gap: 8px; 
          align-items: center; 
        }
        .plans-grid { 
          display: grid; 
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); 
          gap: 14px; 
          padding: 18px 20px; 
        }
        
        .plan-item {
          border-radius: 16px; 
          padding: 18px 14px; 
          text-align: center; 
          cursor: pointer;
          transition: all 0.25s ease;
        }
        .plan-item.affordable { opacity: 1; }
        .plan-item.unaffordable { opacity: 0.6; }
        .plan-item.selected { 
          border: 2px solid #ff0055; 
          background: #fff5f7; 
          transform: scale(1.03); 
          box-shadow: 0 6px 18px rgba(255,0,85,0.15);
        }
        .plan-item.unselected { 
          border: 2px solid #ffe4ec; 
          background: #fff; 
        }
        .plan-item.unselected:hover {
          border-color: #ff80aa;
          transform: translateY(-2px);
        }
        
        .plan-item-icon { font-size: 30px; margin-bottom: 4px; }
        .plan-item-name { 
          font-size: 15px; 
          font-weight: 800; 
          margin: 4px 0 0 0; 
          color: #1a0a10;
        }
        .plan-item-price { 
          font-size: 26px; 
          font-weight: 900; 
          color: #ff0055; 
          margin: 8px 0 10px 0; 
        }
        
        .plans-footer {
          padding: 14px 20px; 
          border-top: 2px solid #fff0f3; 
          display: flex; 
          justify-content: space-between;
          align-items: center; 
          background: #fffafc; 
          flex-wrap: wrap; 
          gap: 10px;
        }
        .plans-footer-bal { 
          font-size: 13px; 
          font-weight: 700; 
          color: #555; 
        }
        .plans-footer-bal span { color: #ff0055; font-weight: 900; }
        .plans-pay-btn.active { 
          background: #ff0055 !important; 
          border-color: #ff0055 !important; 
        }

        /* ============ TRANSACTIONS SECTION ============ */
        .tx-wrapper {
          background: #fff; 
          border: 2px solid #ffe4ec; 
          border-radius: 20px; 
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0,0,0,0.03);
        }
        .tx-header {
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          padding: 14px 20px;
          border-bottom: 2px solid #fff0f3; 
          background: linear-gradient(135deg, #fffafc, #fff5f7);
          flex-wrap: wrap; 
          gap: 10px;
        }
        .tx-header h4 { 
          font-size: 16px; 
          font-weight: 800; 
          color: #1a0a10; 
          margin: 0; 
          display: flex; 
          gap: 8px; 
          align-items: center; 
        }
        .tx-list { padding: 0; }
        
        .tx-item { 
          display: flex; 
          align-items: center; 
          gap: 14px; 
          padding: 14px 20px; 
          border-bottom: 1px solid #fff0f3; 
          transition: background 0.2s;
        }
        .tx-item:last-child { border-bottom: none; }
        .tx-item:hover { background: #fffafc; }
        
        .tx-icon { 
          width: 42px; 
          height: 42px; 
          border-radius: 12px; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          font-size: 18px; 
          border: 1.5px solid; 
          flex-shrink: 0;
        }
        .tx-details { flex: 1; min-width: 0; }
        .tx-details-title { 
          font-size: 13.5px; 
          font-weight: 700; 
          color: #1a0a10; 
          margin: 0 0 2px 0; 
        }
        .tx-details-date { 
          font-size: 11px; 
          color: #bbb; 
          margin: 0; 
          font-weight: 500; 
        }
        .tx-amount { text-align: right; flex-shrink: 0; }
        .tx-amount-text { 
          font-size: 16px; 
          font-weight: 800; 
          margin: 0; 
        }
        .tx-amount-credit { color: #16a34a; }
        .tx-amount-debit { color: #ff0055; }
        
        .tx-empty { text-align: center; padding: 50px 20px; }
        .tx-empty-icon { font-size: 44px; display: block; margin-bottom: 12px; }
        .tx-empty h4 { 
          font-size: 15px; 
          color: #555; 
          margin: 0 0 6px 0; 
          font-weight: 700; 
        }
        .tx-empty p { 
          color: #aaa; 
          font-size: 12px; 
          margin: 0 0 16px 0; 
        }
        .tx-empty-btn { 
          background: #10b981 !important; 
          border-color: #10b981 !important; 
        }
        .tx-empty-btn:hover {
          background: #059669 !important;
          border-color: #059669 !important;
        }

        /* ============ CUSTOM TABS (NO ANTD RADIO) ============ */
        .tx-tabs {
          display: flex;
          gap: 0;
          background: #fff;
          border-radius: 20px;
          border: 1.5px solid #ffe4ec;
          overflow: hidden;
        }
        .tx-tab-btn {
          padding: 5px 16px;
          border: none;
          background: transparent;
          color: #888;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.25s ease;
          font-family: 'Poppins', sans-serif;
          outline: none;
          white-space: nowrap;
        }
        .tx-tab-btn:hover {
          color: #ff0055;
          background: #fff5f7;
        }
        .tx-tab-btn.active {
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          color: #fff;
          box-shadow: 0 3px 10px rgba(255, 0, 85, 0.25);
        }
        .tx-tab-btn:not(:last-child) {
          border-right: 1px solid #ffe4ec;
        }

        /* ============ RESPONSIVE ============ */
        @media (max-width: 640px) {
          .w-page { padding: 14px 10px; }
          .wb-card { padding: 24px 20px; border-radius: 20px; }
          .wb-amount-wrapper { font-size: 40px; }
          .wb-currency { font-size: 24px; }
          .action-card { padding: 14px 16px; }
          .action-title { font-size: 14px; }
          .action-desc { font-size: 11px; }
          .action-icon { width: 40px; height: 40px; font-size: 17px; }
          .plans-grid { grid-template-columns: 1fr; }
          .refer-text h3 { font-size: 14px; }
          .refer-text p { font-size: 11px; }
          .tx-tab-btn { padding: 5px 12px; font-size: 11px; }
        }
      `}</style>

      <div className="w-page">
        <div className="w-wrap">

          {/* ══ BALANCE CARD ══ */}
          <div className="wb-card">
            <div className="wb-top">
              <p className="wb-label">💰 Promotional Balance</p>
              <Button
                className="wb-refresh-btn"
                type="text"
                shape="round"
                icon={<ReloadOutlined spin={refreshing} />}
                onClick={handleRefresh}
              >
                Refresh
              </Button>
            </div>
            <div className="wb-amount-wrapper">
              <span className="wb-currency">₹</span>
              {balance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="wb-note">
              <FaShieldAlt size={11} /> Promotional wallet • Can only be used to buy plans
            </p>
          </div>

          {/* ══ REFERRAL LINK BOX ══ */}
          <div className="referral-box">
            <div className="refer-text">
              <h3><ShareAltOutlined /> Refer & Earn Plans</h3>
              <p>Share this link. When your friend buys their first plan, you get ₹10 in wallet!</p>
            </div>
            <div className="refer-input-wrap">
              <Space.Compact style={{ width: '100%' }}>
                <Input className="refer-input" value={referralLink} readOnly />
                <Tooltip title="Copy Link">
                  <Button className="refer-copy-btn" type="primary" icon={<CopyOutlined />} onClick={copyToClipboard}>
                    Copy
                  </Button>
                </Tooltip>
              </Space.Compact>
            </div>
          </div>

          {/* ══ ACTION CARDS ══ */}
          <div className="action-grid">
            <div className="action-card" onClick={handleShowPlans}>
              <div className="action-icon">
                <FaGem size={20} />
              </div>
              <div className="action-body">
                <h4 className="action-title">Buy Plans using Wallet</h4>
                <p className="action-desc">Use your earned balance to activate plans</p>
              </div>
              <div className="action-arrow">→</div>
            </div>
          </div>

          {/* ══ BUY PLANS SECTION ══ */}
          {showPlans && (
            <div className="plans-wrapper">
              <div className="plans-header">
                <h4><FaGem color="#ff0055" /> Available Plans</h4>
                <Button type="default" danger shape="round" size="small" onClick={() => setShowPlans(false)}>
                  ✕ Close
                </Button>
              </div>

              <div className="plans-grid">
                {plans.map(plan => {
                  const price = Number(plan.price);
                  const canAfford = balance >= price;
                  const isSelected = selectedPlan === plan.id;

                  return (
                    <div
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`plan-item ${canAfford ? 'affordable' : 'unaffordable'} ${isSelected ? 'selected' : 'unselected'}`}
                    >
                      <div className="plan-item-icon">💎</div>
                      <h5 className="plan-item-name">{plan.plan_name}</h5>
                      <div className="plan-item-price">₹{price}</div>
                      {!canAfford ? (
                        <Tag color="error" icon={<CloseCircleOutlined />}>Need ₹{price - balance} more</Tag>
                      ) : isSelected ? (
                        <Tag color="success" icon={<CheckCircleOutlined />}>Selected</Tag>
                      ) : (
                        <Tag color="processing">Tap to Select</Tag>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="plans-footer">
                <span className="plans-footer-bal">
                  Balance: <span>₹{balance}</span>
                </span>
                <Button
                  type="primary"
                  shape="round"
                  size="large"
                  icon={<WalletOutlined />}
                  onClick={handleWalletPurchase}
                  loading={buyLoading}
                  disabled={!selectedPlan || balance < Number(plans.find(p => p.id === selectedPlan)?.price || 0)}
                  className={selectedPlan && balance >= Number(plans.find(p => p.id === selectedPlan)?.price || 0) ? "plans-pay-btn active" : "plans-pay-btn"}
                >
                  {buyLoading ? "Processing..." : "Pay from Wallet"}
                </Button>
              </div>
            </div>
          )}

          {/* ══ TRANSACTIONS ══ */}
          <div className="tx-wrapper">
            <div className="tx-header">
              <h4>
                <HistoryOutlined style={{ color: "#ff0055" }} /> Transactions
              </h4>

              {/* ✅ CUSTOM TABS - 100% CSS CONTROL */}
              <div className="tx-tabs">
                {[
                  { key: "all", label: "All" },
                  { key: "credit", label: "💚 Earned" },
                  { key: "debit", label: "🔴 Spent" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    className={`tx-tab-btn ${activeTab === tab.key ? "active" : ""}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="tx-list">
              {filteredTransactions.length > 0 ? (
                filteredTransactions.map((tx, idx) => {
                  const src = getSourceConfig(tx.source);
                  const isCredit = tx.transaction_type === "credit";
                  return (
                    <div key={tx.id || idx} className="tx-item">
                      <div className="tx-icon" style={{ background: src.bg, borderColor: src.border }}>
                        {src.icon}
                      </div>
                      <div className="tx-details">
                        <p className="tx-details-title">{tx.description || src.label}</p>
                        <p className="tx-details-date">{formatDate(tx.created_at)}</p>
                      </div>
                      <div className="tx-amount">
                        <p className={`tx-amount-text ${isCredit ? 'tx-amount-credit' : 'tx-amount-debit'}`}>
                          {isCredit ? "+" : "-"}₹{Number(tx.amount).toLocaleString("en-IN")}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="tx-empty">
                  <span className="tx-empty-icon">💸</span>
                  <h4>No transactions yet</h4>
                  <p>Refer friends to earn balance!</p>
                  <Button
                    className="tx-empty-btn"
                    type="primary"
                    shape="round"
                    icon={<FaUserFriends style={{ marginRight: 6 }} />}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Start Referring
                  </Button>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Wallet;